/**
 * Paddiq Design Tokens
 * Single source of truth for all colour, typography, spacing and shape values.
 * Matches the Paddiq design mockups: purple primary, dark-navy surfaces,
 * full light + dark mode support.
 */

// ─── Colour Palette ───────────────────────────────────────────────────────────

export const colors = {
  // ── Brand purple (primary) ──────────────────────────────────────────────────
  primary: {
    50:  '#f3eeff',
    100: '#e4d4ff',
    200: '#cbaeff',
    300: '#a97ef9',
    400: '#8b55f6',
    500: '#7C3AED', // main – Paddiq violet
    600: '#6d28d9',
    700: '#5b21b6',
    800: '#4c1d95',
    900: '#3b1678',
    A100: '#d9b8ff',
    A200: '#b57bff',
    A400: '#8b40ff',
    A700: '#7826ff',
  },

  // ── Secondary – pit-lane amber ─────────────────────────────────────────────
  secondary: {
    50:  '#fffbea',
    100: '#fff3c4',
    200: '#ffe88a',
    300: '#ffd84d',
    400: '#ffc72b',
    500: '#FFAB00', // main
    600: '#e69900',
    700: '#cc8800',
    800: '#a36e00',
    900: '#7a5200',
    A100: '#ffe680',
    A200: '#ffd740',
    A400: '#ffc400',
    A700: '#ffab00',
  },

  // ── Semantic ────────────────────────────────────────────────────────────────
  error: {
    light: '#f87171',
    main:  '#ef4444',
    dark:  '#dc2626',
    contrastText: '#ffffff',
  },
  warning: {
    light: '#fb923c',
    main:  '#f97316',
    dark:  '#ea580c',
    contrastText: '#ffffff',
  },
  info: {
    light: '#38bdf8',
    main:  '#0ea5e9',
    dark:  '#0284c7',
    contrastText: '#ffffff',
  },
  success: {
    light: '#4ade80',
    main:  '#22c55e',
    dark:  '#16a34a',
    contrastText: '#ffffff',
  },

  // ── Neutral greys ───────────────────────────────────────────────────────────
  grey: {
    50:  '#f9fafb',
    100: '#f3f4f6',
    200: '#e5e7eb',
    300: '#d1d5db',
    400: '#9ca3af',
    500: '#6b7280',
    600: '#4b5563',
    700: '#374151',
    800: '#1f2937',
    900: '#111827',
  },

  // ── Dark theme surfaces ────────────────────────────────────────────────────
  dark: {
    bg:       '#0B0B1A',
    sidebar:  '#09090B',
    surface:  '#13132B',
    elevated: '#1A1A38',
    border:   'rgba(255,255,255,0.07)',
    hover:    'rgba(255,255,255,0.05)',
  },

  // ── Light theme surfaces ───────────────────────────────────────────────────
  light: {
    bg:       '#F5F6FA',
    sidebar:  '#FAFAFA',
    surface:  '#FFFFFF',
    elevated: '#FFFFFF',
    border:   'rgba(0,0,0,0.08)',
    hover:    'rgba(0,0,0,0.04)',
  },

  // ── Sidebar chrome (nav, profile, team selector) ─────────────────────────
  sidebarChrome: {
    dark: {
      text:            '#F4F4F5',
      textSecondary:   '#A1A1AA',
      textMuted:       '#71717A',
      border:          'rgba(255,255,255,0.08)',
      navText:         '#B8B8C0',
      navHover:        'rgba(255,255,255,0.05)',
      navActiveBg:     '#1C192E',
      navActiveText:   '#E7E1FF',
      navActiveIcon:   '#A78BFA',
      navActiveHover:  '#242039',
      teamCardBg:      'transparent',
      profileCardBg:   '#111113',
      menuBg:          '#111113',
      menuText:        '#F4F4F5',
      collapseText:    '#A1A1AA',
      profileAvatarBg: '#27272A',
      profileAvatarBorder: 'rgba(255,255,255,0.12)',
      teamAvatarBg:    '#18181B',
      teamAvatarText:  '#D4D4D8',
      teamAvatarBorder:'rgba(255,255,255,0.1)',
    },
    light: {
      text:            '#18181B',
      textSecondary:   '#71717A',
      textMuted:       '#A1A1AA',
      border:          '#E4E4E7',
      navText:         '#3F3F46',
      navHover:        '#F1F1F3',
      navActiveBg:     '#EFEDFA',
      navActiveText:   '#4C3B88',
      navActiveIcon:   '#6D5BD0',
      navActiveHover:  '#E8E4F8',
      teamCardBg:      'transparent',
      profileCardBg:   '#FFFFFF',
      menuBg:          '#FFFFFF',
      menuText:        '#18181B',
      collapseText:    '#71717A',
      profileAvatarBg: '#27272A',
      profileAvatarBorder: '#E4E4E7',
      teamAvatarBg:    '#F4F4F5',
      teamAvatarText:  '#52525B',
      teamAvatarBorder:'#E4E4E7',
    },
  },

  // ── Page header chrome (titles, toolbar buttons, replay bar, weather) ───────
  headerChrome: {
    dark: {
      sectionLabel:      '#94A3B8',
      buttonBorder:      'rgba(36,62,82,0.78)',
      buttonText:        '#FFFFFF',
      buttonBg:          'rgba(1,13,24,0.58)',
      buttonBorderHover: 'rgba(80,113,138,0.9)',
      buttonBgHover:     'rgba(255,255,255,0.04)',
      buttonSecondary:   '#64748B',
      replayBarBorder:   'rgba(80,113,138,0.52)',
      replayBarBg:       'rgba(4,15,27,0.92)',
      replayBarShadow:   'inset 0 1px 0 rgba(255,255,255,0.05), 0 8px 22px rgba(0,0,0,0.22)',
      replayTimeText:    '#CBD5E1',
      replayMetaText:    '#64748B',
      replayMetaBg:      'rgba(255,255,255,0.05)',
      replayMetaBorder:  'rgba(255,255,255,0.08)',
      replayMetaLabel:   '#D8DEE8',
      replayPlayBorder:  'rgba(183,140,255,0.58)',
      replayPlayBg:      'rgba(183,140,255,0.24)',
      replayPlayBgActive:'rgba(183,140,255,0.38)',
      replayPlayBgHover: 'rgba(183,140,255,0.48)',
      weatherTitle:      '#FFFFFF',
      weatherSubtitle:   '#A6B0BE',
      weatherHoverBg:    'rgba(255,255,255,0.07)',
      weatherDivider:    'rgba(36,62,82,0.72)',
      mapDockBorder:     'rgba(36,62,82,0.72)',
      mapDockBg:         'rgba(2,10,18,0.94)',
      mapDockShadow:     '0 12px 32px rgba(0,0,0,0.42)',
      mapDockLegendBg:   'rgba(255,255,255,0.03)',
      mapDockLegendText: '#A6B0BE',
    },
    light: {
      sectionLabel:      '#6B7280',
      buttonBorder:      '#E5E7EB',
      buttonText:        '#1F2937',
      buttonBg:          '#FFFFFF',
      buttonBorderHover: '#D1D5DB',
      buttonBgHover:     '#F9FAFB',
      buttonSecondary:   '#6B7280',
      replayBarBorder:   '#D1D5DB',
      replayBarBg:       '#FFFFFF',
      replayBarShadow:   'none',
      replayTimeText:    '#374151',
      replayMetaText:    '#6B7280',
      replayMetaBg:      '#F9FAFB',
      replayMetaBorder:  '#E5E7EB',
      replayMetaLabel:   '#374151',
      replayPlayBorder:  'rgba(124,58,237,0.35)',
      replayPlayBg:      'rgba(124,58,237,0.12)',
      replayPlayBgActive:'rgba(124,58,237,0.2)',
      replayPlayBgHover: 'rgba(124,58,237,0.18)',
      weatherTitle:      '#1F2937',
      weatherSubtitle:   '#6B7280',
      weatherHoverBg:    'rgba(0,0,0,0.04)',
      weatherDivider:    '#E5E7EB',
      mapDockBorder:     '#E5E7EB',
      mapDockBg:         'rgba(255,255,255,0.98)',
      mapDockShadow:     '0 10px 28px rgba(15,23,42,0.12)',
      mapDockLegendBg:   '#F9FAFB',
      mapDockLegendText: '#374151',
    },
  },

  // ── Lap meta drawer (bottom sheet on Sessions map) ─────────────────────────
  lapMetaChrome: {
    dark: {
      paperBg:         '#010D18',
      border:          'rgba(36,62,82,0.82)',
      headerBorder:    'rgba(36,62,82,0.72)',
      title:           '#F8FAFC',
      subtitle:        '#94A3B8',
      toggleText:      '#94A3B8',
      toggleBorder:    'rgba(36,62,82,0.72)',
      toggleSelectedText: '#FFFFFF',
      toggleSelectedBg:   'rgba(168,85,247,0.18)',
      closeBorder:     'rgba(36,62,82,0.72)',
      closeText:       '#CBD5E1',
      emptyText:       '#94A3B8',
      tableBorder:     'rgba(36,62,82,0.72)',
      tableBg:         'rgba(1,13,24,0.48)',
      tableHeadBg:     '#010D18',
      tableHeadText:   '#94A3B8',
      tableHeadBorder: 'rgba(36,62,82,0.82)',
      rowBorder:       'rgba(36,62,82,0.36)',
      lapText:         '#F8FAFC',
      kindText:        '#C4B5FD',
      monoText:        '#E2E8F0',
      utcText:         '#CBD5E1',
      mutedText:       '#94A3B8',
    },
    light: {
      paperBg:         '#FFFFFF',
      border:          '#E5E7EB',
      headerBorder:    '#E5E7EB',
      title:           '#111827',
      subtitle:        '#6B7280',
      toggleText:      '#6B7280',
      toggleBorder:    '#E5E7EB',
      toggleSelectedText: '#7C3AED',
      toggleSelectedBg:   'rgba(124,58,237,0.12)',
      closeBorder:     '#E5E7EB',
      closeText:       '#6B7280',
      emptyText:       '#6B7280',
      tableBorder:     '#E5E7EB',
      tableBg:         '#FFFFFF',
      tableHeadBg:     '#F9FAFB',
      tableHeadText:   '#6B7280',
      tableHeadBorder: '#E5E7EB',
      rowBorder:       '#F3F4F6',
      lapText:         '#111827',
      kindText:        '#7C3AED',
      monoText:        '#374151',
      utcText:         '#4B5563',
      mutedText:       '#9CA3AF',
    },
  },

  // ── Session & laps drawer (right panel on Sessions map) ────────────────────
  sessionDrawerChrome: {
    dark: {
      paperBg:              '#010D18',
      border:               'rgba(36,62,82,0.72)',
      headerBorder:         'rgba(36,62,82,0.72)',
      title:                '#FFFFFF',
      sectionLabel:         '#94A3B8',
      body:                 '#64748B',
      muted:                '#64748B',
      empty:                '#94A3B8',
      error:                '#F87171',
      resetText:            '#94A3B8',
      resetBorder:          'rgba(36,62,82,0.72)',
      resetHoverBg:         'rgba(255,255,255,0.04)',
      closeText:            '#94A3B8',
      closeBorder:          'rgba(36,62,82,0.72)',
      divider:              'rgba(36,62,82,0.72)',
      cardBg:               'rgba(1,13,24,0.46)',
      cardBorder:           'rgba(36,62,82,0.72)',
      collapsibleTitle:     '#CBD5E1',
      collapsibleTitleCollapsed: '#94A3B8',
      collapsibleSummary:   '#64748B',
      collapsibleIcon:      '#64748B',
      removeHoverText:      '#F87171',
      removeHoverBg:        'rgba(239,68,68,0.08)',
      addCompareText:       '#E2E8F0',
      addCompareDisabledText: '#64748B',
      addCompareBorder:     'rgba(36,62,82,0.72)',
      toggleText:           '#94A3B8',
      toggleBorder:         'rgba(36,62,82,0.72)',
      toggleSelectedText:   '#FFFFFF',
      toggleSelectedBg:     'rgba(255,255,255,0.06)',
      label:                '#94A3B8',
      inputText:            '#FFFFFF',
      inputBg:              'rgba(1,13,24,0.46)',
      inputBorder:          'rgba(36,62,82,0.72)',
      inputBorderHover:     'rgba(80,113,138,0.9)',
      menuBg:               '#010D18',
      menuBorder:           'rgba(36,62,82,0.72)',
      menuShadow:           '0 12px 32px rgba(0,0,0,0.45)',
      menuPrimaryText:      '#FFFFFF',
      menuSecondaryText:    '#94A3B8',
      menuSubheaderBg:      '#010D18',
      menuSubheaderText:    '#64748B',
      sectionCardBg:        'rgba(1,13,24,0.55)',
      sectionCardBorder:    'rgba(36,62,82,0.55)',
      tableBg:              'rgba(1,13,24,0.46)',
      tableBorder:          'rgba(36,62,82,0.72)',
      tableHeadBg:          '#010D18',
      tableHeadText:        '#94A3B8',
      tableHeadBorder:      'rgba(36,62,82,0.72)',
      tableRowBorder:       'rgba(36,62,82,0.36)',
      lapTimeText:          '#E2E8F0',
      lapGapText:           '#94A3B8',
      lapOut:               '#FBBF24',
      lapOutSelected:       '#FDE68A',
      lapOutSelectedBg:     'rgba(245,158,11,0.14)',
      lapIn:                '#94A3B8',
      lapInSelected:        '#E2E8F0',
      lapInSelectedBg:      'rgba(148,163,184,0.12)',
      lapCrash:             '#F87171',
      lapCrashSelected:     '#FCA5A5',
      lapCrashSelectedBg:   'rgba(239,68,68,0.14)',
      lapFlying:            '#E2E8F0',
      lapFlyingSelected:    '#DDD6FE',
      lapFlyingSelectedBg:  'rgba(124,58,237,0.13)',
      lapCompareSelectedBg: 'rgba(245,158,11,0.1)',
      lapCompareHoverBg:  'rgba(245,158,11,0.06)',
      lapPrimaryHoverBg:  'rgba(124,58,237,0.08)',
      footageChipText:    '#D1FAE5',
      footageChipBg:      'rgba(16,185,129,0.16)',
      footageChipBorder:  'rgba(52,211,153,0.35)',
      footageChipIcon:    '#6EE7B7',
    },
    light: {
      paperBg:              '#FFFFFF',
      border:               '#E5E7EB',
      headerBorder:         '#E5E7EB',
      title:                '#111827',
      sectionLabel:         '#6B7280',
      body:                 '#6B7280',
      muted:                '#9CA3AF',
      empty:                '#6B7280',
      error:                '#DC2626',
      resetText:            '#6B7280',
      resetBorder:          '#E5E7EB',
      resetHoverBg:         'rgba(0,0,0,0.04)',
      closeText:            '#6B7280',
      closeBorder:          '#E5E7EB',
      divider:              '#E5E7EB',
      cardBg:               '#F9FAFB',
      cardBorder:           '#E5E7EB',
      collapsibleTitle:     '#111827',
      collapsibleTitleCollapsed: '#6B7280',
      collapsibleSummary:   '#6B7280',
      collapsibleIcon:      '#9CA3AF',
      removeHoverText:      '#DC2626',
      removeHoverBg:        'rgba(220,38,38,0.08)',
      addCompareText:       '#374151',
      addCompareDisabledText: '#9CA3AF',
      addCompareBorder:     '#E5E7EB',
      toggleText:           '#6B7280',
      toggleBorder:         '#E5E7EB',
      toggleSelectedText:   '#7C3AED',
      toggleSelectedBg:     'rgba(124,58,237,0.12)',
      label:                '#6B7280',
      inputText:            '#111827',
      inputBg:              '#FFFFFF',
      inputBorder:          '#D1D5DB',
      inputBorderHover:     '#7C3AED',
      menuBg:               '#FFFFFF',
      menuBorder:           '#E5E7EB',
      menuShadow:           '0 12px 32px rgba(15,23,42,0.12)',
      menuPrimaryText:      '#111827',
      menuSecondaryText:    '#6B7280',
      menuSubheaderBg:      '#F9FAFB',
      menuSubheaderText:    '#9CA3AF',
      sectionCardBg:        '#F9FAFB',
      sectionCardBorder:    '#E5E7EB',
      tableBg:              '#FFFFFF',
      tableBorder:          '#E5E7EB',
      tableHeadBg:          '#F9FAFB',
      tableHeadText:        '#6B7280',
      tableHeadBorder:      '#E5E7EB',
      tableRowBorder:       '#F3F4F6',
      lapTimeText:          '#374151',
      lapGapText:           '#6B7280',
      lapOut:               '#D97706',
      lapOutSelected:       '#B45309',
      lapOutSelectedBg:     'rgba(245,158,11,0.12)',
      lapIn:                '#6B7280',
      lapInSelected:        '#374151',
      lapInSelectedBg:      'rgba(148,163,184,0.12)',
      lapCrash:             '#DC2626',
      lapCrashSelected:     '#B91C1C',
      lapCrashSelectedBg:   'rgba(239,68,68,0.12)',
      lapFlying:            '#374151',
      lapFlyingSelected:    '#7C3AED',
      lapFlyingSelectedBg:  'rgba(124,58,237,0.12)',
      lapCompareSelectedBg: 'rgba(245,158,11,0.1)',
      lapCompareHoverBg:  'rgba(245,158,11,0.06)',
      lapPrimaryHoverBg:  'rgba(124,58,237,0.08)',
      footageChipText:    '#047857',
      footageChipBg:      'rgba(16,185,129,0.12)',
      footageChipBorder:  'rgba(16,185,129,0.25)',
      footageChipIcon:    '#059669',
    },
  },

  // ── Footage upload drawer (right panel on Sessions map) ────────────────────
  footageChrome: {
    dark: {
      paperBg:          '#010D18',
      border:           'rgba(36,62,82,0.72)',
      headerBorder:     'rgba(36,62,82,0.72)',
      title:            '#FFFFFF',
      subtitle:         '#94A3B8',
      body:             '#94A3B8',
      sectionTitle:     '#E2E8F0',
      cardBg:           'rgba(3,18,32,0.78)',
      cardBgMuted:      'rgba(3,18,32,0.58)',
      cardBorder:       'rgba(36,62,82,0.72)',
      label:            '#94A3B8',
      value:            '#E5F0FF',
      monoValue:        '#CBD5E1',
      fileName:         '#F8FAFC',
      expandIcon:       '#DCEBFF',
      expandBorder:     'rgba(148,163,184,0.28)',
      deleteIcon:       '#FCA5A5',
      deleteBorder:     'rgba(248,113,113,0.35)',
      sourceChipText:   '#DCEBFF',
      sourceChipBg:     'rgba(52,211,153,0.14)',
      matchChipText:    '#D1FAE5',
      matchChipBg:      'rgba(16,185,129,0.16)',
      neutralChipText:  '#E2E8F0',
      neutralChipBg:    'rgba(148,163,184,0.12)',
      inputLabel:       '#94A3B8',
      inputLabelFocus:  '#A78BFA',
      inputText:        '#E5F0FF',
      inputBg:          'rgba(15,23,42,0.62)',
      inputBorder:      'rgba(71,85,105,0.72)',
      inputBorderHover: 'rgba(167,139,250,0.78)',
      inputBorderFocus: '#8B5CF6',
      helperText:       '#94A3B8',
    },
    light: {
      paperBg:          '#FFFFFF',
      border:           '#E5E7EB',
      headerBorder:     '#E5E7EB',
      title:            '#111827',
      subtitle:         '#6B7280',
      body:             '#6B7280',
      sectionTitle:     '#111827',
      cardBg:           '#F9FAFB',
      cardBgMuted:      '#FFFFFF',
      cardBorder:       '#E5E7EB',
      label:            '#6B7280',
      value:            '#374151',
      monoValue:        '#4B5563',
      fileName:         '#111827',
      expandIcon:       '#374151',
      expandBorder:     '#E5E7EB',
      deleteIcon:       '#DC2626',
      deleteBorder:     'rgba(220,38,38,0.25)',
      sourceChipText:   '#047857',
      sourceChipBg:     'rgba(16,185,129,0.12)',
      matchChipText:    '#047857',
      matchChipBg:      'rgba(16,185,129,0.12)',
      neutralChipText:  '#4B5563',
      neutralChipBg:    '#F3F4F6',
      inputLabel:       '#6B7280',
      inputLabelFocus:  '#7C3AED',
      inputText:        '#111827',
      inputBg:          '#FFFFFF',
      inputBorder:      '#D1D5DB',
      inputBorderHover: '#7C3AED',
      inputBorderFocus: '#7C3AED',
      helperText:       '#6B7280',
    },
  },

  // ── Lap traces panel (bottom sheet on Sessions map) ────────────────────────
  traceChrome: {
    dark: {
      panelBg:              'rgba(1,13,24,0.96)',
      shellBg:              '#010D18',
      shellHeaderBg:        'rgba(1,13,24,0.98)',
      border:               'rgba(36,62,82,0.82)',
      headerBorder:         'rgba(36,62,82,0.54)',
      title:                '#FFFFFF',
      subtitle:             '#64748B',
      sectionLabel:         '#A6B0BE',
      meta:                 '#64748B',
      chartSubtitle:        '#A6B0BE',
      liveValue:            '#FFFFFF',
      chartRowBorder:       'rgba(36,62,82,0.42)',
      gridStroke:           'rgba(255,255,255,0.04)',
      tooltipBg:            'rgba(1,13,24,0.96)',
      tooltipBorder:        'rgba(36,62,82,0.78)',
      cursorLine:           'rgba(255,255,255,0.88)',
      cursorHoverLine:      'rgba(148,163,184,0.55)',
      cursorLabelBg:        'rgba(1,13,24,0.94)',
      cursorLabelBorder:    'rgba(36,62,82,0.78)',
      cursorLabelText:      '#FFFFFF',
      pickerBg:             '#010D18',
      pickerBorder:         'rgba(36,62,82,0.82)',
      pickerItemText:       '#E2E8F0',
      pickerItemSubtext:    '#64748B',
      pickerItemBorder:     'rgba(36,62,82,0.22)',
      pickerItemHover:      'rgba(255,255,255,0.04)',
      pickerItemSelectedBg: 'rgba(183,140,255,0.12)',
      pickerItemSelectedBorder: 'rgba(183,140,255,0.28)',
      pickerCheckbox:       '#64748B',
      tracesButtonBorder:   'rgba(36,62,82,0.72)',
      tracesButtonBg:       'rgba(1,13,24,0.5)',
      tracesButtonActiveBg: 'rgba(124,58,237,0.9)',
      tracesButtonHoverBorder: 'rgba(183,140,255,0.68)',
      tracesButtonHoverBg:  'rgba(255,255,255,0.05)',
      iconAccent:           '#A855F7',
      closeIcon:            '#A6B0BE',
      closeBorder:          'rgba(36,62,82,0.72)',
      emptyText:            '#64748B',
    },
    light: {
      panelBg:              '#FFFFFF',
      shellBg:              '#FFFFFF',
      shellHeaderBg:        '#FFFFFF',
      border:               '#E5E7EB',
      headerBorder:         '#E5E7EB',
      title:                '#111827',
      subtitle:             '#6B7280',
      sectionLabel:         '#6B7280',
      meta:                 '#6B7280',
      chartSubtitle:        '#6B7280',
      liveValue:            '#111827',
      chartRowBorder:       '#F3F4F6',
      gridStroke:           'rgba(15,23,42,0.08)',
      tooltipBg:            '#FFFFFF',
      tooltipBorder:        '#E5E7EB',
      cursorLine:           '#7C3AED',
      cursorHoverLine:      'rgba(124,58,237,0.45)',
      cursorLabelBg:        '#FFFFFF',
      cursorLabelBorder:    '#E5E7EB',
      cursorLabelText:      '#111827',
      pickerBg:             '#FFFFFF',
      pickerBorder:         '#E5E7EB',
      pickerItemText:       '#111827',
      pickerItemSubtext:    '#6B7280',
      pickerItemBorder:     '#E5E7EB',
      pickerItemHover:      'rgba(0,0,0,0.03)',
      pickerItemSelectedBg: '#F8FAFC',
      pickerItemSelectedBorder: '#CBD5E1',
      pickerCheckbox:       '#9CA3AF',
      tracesButtonBorder:   '#E5E7EB',
      tracesButtonBg:       '#FFFFFF',
      tracesButtonActiveBg: '#F3F4F6',
      tracesButtonHoverBorder: '#D1D5DB',
      tracesButtonHoverBg:  '#F9FAFB',
      iconAccent:           '#7C3AED',
      closeIcon:            '#6B7280',
      closeBorder:          '#E5E7EB',
      emptyText:            '#6B7280',
    },
  },

  // ── Racing status chips ───────────────────────────────────────────────────
  status: {
    active:      { text: '#22c55e', bg: '#22c55e1a', border: '#22c55e33' },
    injured:     { text: '#ef4444', bg: '#ef44441a', border: '#ef444433' },
    inactive:    { text: '#9ca3af', bg: '#9ca3af1a', border: '#9ca3af33' },
    inUse:       { text: '#22c55e', bg: '#22c55e1a', border: '#22c55e33' },
    available:   { text: '#9ca3af', bg: '#9ca3af1a', border: '#9ca3af33' },
    maintenance: { text: '#f97316', bg: '#f973161a', border: '#f9731633' },
    inShop:      { text: '#f97316', bg: '#f973161a', border: '#f9731633' },
    overdue:     { text: '#ef4444', bg: '#ef44441a', border: '#ef444433' },
    due5days:    { text: '#f97316', bg: '#f973161a', border: '#f9731633' },
    due7days:    { text: '#eab308', bg: '#eab3081a', border: '#eab30833' },
    race:        { text: '#7C3AED', bg: '#7C3AED1a', border: '#7C3AED33' },
    practice:    { text: '#0ea5e9', bg: '#0ea5e91a', border: '#0ea5e933' },
    test:        { text: '#6b7280', bg: '#6b72801a', border: '#6b728033' },
    qualifying:  { text: '#a78bfa', bg: '#a78bfa1a', border: '#a78bfa33' },
    positive:    { text: '#22c55e', bg: '#22c55e1a', border: '#22c55e33' },
    neutral:     { text: '#9ca3af', bg: '#9ca3af1a', border: '#9ca3af33' },
    needsWork:   { text: '#f97316', bg: '#f973161a', border: '#f9731633' },
  },
} as const;

export function getSidebarChrome(isDark: boolean) {
  return isDark ? colors.sidebarChrome.dark : colors.sidebarChrome.light;
}

// ─── Typography ───────────────────────────────────────────────────────────────

export const typography = {
  fontFamilyBase: '"Inter", "Roboto", "Helvetica Neue", Arial, sans-serif',
  fontFamilyMono: '"JetBrains Mono", "Fira Code", "Courier New", monospace',
  fontWeightLight: 300,
  fontWeightRegular: 400,
  fontWeightMedium: 500,
  fontWeightSemiBold: 600,
  fontWeightBold: 700,
  fontSizeBase: 14,
  scale: {
    xs: '0.75rem',   // 12px
    sm: '0.875rem',  // 14px
    md: '1rem',      // 16px
    lg: '1.125rem',  // 18px
    xl: '1.25rem',   // 20px
    '2xl': '1.5rem', // 24px
    '3xl': '1.875rem',// 30px
    '4xl': '2.25rem', // 36px
    '5xl': '3rem',    // 48px
  },
} as const;

// ─── Spacing ──────────────────────────────────────────────────────────────────

/** MUI-compatible spacing unit (px). All spacing is a multiple of this. */
export const spacingUnit = 8;

// ─── Shape ────────────────────────────────────────────────────────────────────

export const shape = {
  borderRadius: 12,
  borderRadiusSm: 6,
  borderRadiusMd: 12,
  borderRadiusLg: 20,
  borderRadiusFull: 9999,
} as const;

// ─── Shadows ──────────────────────────────────────────────────────────────────

export const shadows = {
  card: '0 2px 16px rgba(0,0,0,0.4)',
  elevated: '0 8px 32px rgba(0,0,0,0.6)',
  glow: `0 0 20px rgba(76,175,80,0.3)`,
} as const;

// ─── Z-index ──────────────────────────────────────────────────────────────────

export const zIndex = {
  sidebar: 1200,
  header: 1100,
  modal: 1300,
  tooltip: 1500,
  snackbar: 1400,
} as const;

// ─── Breakpoints ──────────────────────────────────────────────────────────────

export const breakpoints = {
  xs: 0,
  sm: 600,
  md: 900,
  lg: 1200,
  xl: 1536,
} as const;
